import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Check, AutoAwesome } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ParentLoginDialog from "../../pages/ParentLogin";
import { createOrder, verifyPayment, createSubscription, verifySubscription } from "../../api/parentApi";
import { scheduleDowngradePlan } from "../../api/parentApi";
import { RAZORPAY_KEY_ID, parentPlans } from "../../constants/razorpay";

/* =========================
   These PLANS will be removed in future; only parent plans will be shown from razorpay.js
========================= */
const schoolPlans = [];
const customPlan = {};

/* Plan purchase CTA hidden for now; cards show "Book a Demo" instead */
const showPlanCta = false;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/* =========================
   PARENT PLAN CARD
========================= */
const ParentPlanCard = ({ plan, selectedVariant, setSelectedVariant, isLoggedIn, currentPlanPrice, storedToken, parentData, setParentData, handleSubscribe, loading, openLogin, setOpenLogin }) => {
  const navigate = useNavigate();
  const variant = selectedVariant[plan.id] || plan.variants[0];
  const price = plan.price;   // price comes from the plan object, not the Razorpay variant
  const isPopular = plan.tag === "Most Popular";
  const isBestValue = plan.tag === "Best Value";
  const isPremiumCard = isPopular || isBestValue;

  let buttonText = plan.cta;
  let buttonAction = () => setOpenLogin(true);
  let buttonDisabled = false;
  let isCurrentPlan = false;

  if (isLoggedIn) {
    if (price === currentPlanPrice) {
      buttonText = "Current Plan";
      buttonDisabled = true;
      isCurrentPlan = true;
    } else if (price > currentPlanPrice) {
      buttonText = "Upgrade";
      buttonAction = () => handleSubscribe(plan, variant);
    } else {
      buttonText = "Downgrade";
      buttonAction = async () => {
        const response = await scheduleDowngradePlan({ plan_key: variant.id, plan_name: plan.title }, storedToken);
        if (response.meta?.success) {
          const expiryDate = new Date(response.response.expiry_date);
          const formattedDate = expiryDate.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
          alert(`${response.response.plan_name} Plan (Current),\nDowngrade scheduled on ${formattedDate} to ${response.response.next_plan_name}`);
        }
      };
    }
  }

  // Current plan gets its own distinct look
  const isActiveCard = isPremiumCard || isCurrentPlan;
  const outerGradient = isCurrentPlan
    ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    : isPremiumCard
      ? plan.accentGradient || "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)"
      : "#fff";
  const outerGlow = isCurrentPlan
    ? "0 20px 60px rgba(16,185,129,0.35)"
    : isPremiumCard
      ? "0 20px 60px rgba(79,110,247,0.3)"
      : "0 4px 20px rgba(13,27,75,0.07)";
  const outerGlowHover = isCurrentPlan
    ? "0 30px 80px rgba(16,185,129,0.5)"
    : isPremiumCard
      ? "0 30px 80px rgba(79,110,247,0.4)"
      : "0 20px 60px rgba(13,27,75,0.12)";

  return (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        borderRadius: "24px",
        background: outerGradient,
        p: isActiveCard ? "2px" : 0,
        boxShadow: outerGlow,
        transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: outerGlowHover,
        },
      }}
    >
      {/* Popular badge */}
      {plan.tag && (
        <Box
          sx={{
            position: "absolute",
            top: -14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 2,
              py: 0.6,
              borderRadius: "100px",
              background: isPremiumCard ? "#fff" : "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
              color: isPremiumCard ? "#4F6EF7" : "#fff",
              fontWeight: 700,
              fontSize: "0.78rem",
              boxShadow: "0 4px 16px rgba(79,110,247,0.3)",
              whiteSpace: "nowrap",
            }}
          >
            <AutoAwesome sx={{ fontSize: 13 }} />
            {plan.tag}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          height: "100%",
          borderRadius: isActiveCard ? "22px" : "24px",
          bgcolor: isCurrentPlan ? "#065F46" : isPremiumCard ? "#0D1B4B" : "#fff",
          border: !isActiveCard ? "1.5px solid rgba(13,27,75,0.07)" : "none",
          p: { xs: 3.5, md: 4 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          fontWeight={800}
          mb={0.5}
          color={isActiveCard ? "#fff" : "#0D1B4B"}
        >
          {plan.title}
        </Typography>

        <Typography
          variant="body2"
          mb={3}
          sx={{ color: isActiveCard ? "rgba(255,255,255,0.6)" : "#64748B", lineHeight: 1.6 }}
        >
          {plan.subtitle}
        </Typography>

        {/* Variant selector */}
        {plan.variants.length > 1 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {plan.variants.map((v) => (
              <Button
                key={v.id}
                size="small"
                variant={variant.id === v.id ? "contained" : "outlined"}
                sx={{
                  minWidth: "auto",
                  px: 2,
                  py: 0.6,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  borderColor: isActiveCard ? "rgba(255,255,255,0.3)" : undefined,
                  color: variant.id === v.id ? "#fff" : (isActiveCard ? "rgba(255,255,255,0.7)" : undefined),
                }}
                onClick={() => setSelectedVariant({ ...selectedVariant, [plan.id]: v })}
              >
                {v.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Price */}
        <Box display="flex" alignItems="baseline" gap={0.5} mb={0.5}>
          <Typography
            sx={{
              fontSize: "2.4rem",
              fontWeight: 900,
              lineHeight: 1,
              color: isActiveCard ? "#fff" : "#0D1B4B",
            }}
          >
            ₹{price}
          </Typography>
          <Typography variant="body2" sx={{ color: isActiveCard ? "rgba(255,255,255,0.5)" : "#94a3b8" }}>
            /student/year
          </Typography>
        </Box>

        {price === 0 && (
          <Typography variant="caption" sx={{ color: isActiveCard ? "rgba(255,255,255,0.5)" : "#94a3b8", mb: 3 }}>
            Free forever
          </Typography>
        )}

        <Divider sx={{ my: 3, borderColor: isActiveCard ? "rgba(255,255,255,0.12)" : "rgba(13,27,75,0.07)" }} />

        {/* Features */}
        <Stack spacing={1.5} flex={1} mb={4}>
          {plan.features.map((f, i) => (
            <Box key={i} display="flex" alignItems="flex-start" gap={1.5}>
              <Check
                sx={{
                  fontSize: 18,
                  mt: 0.1,
                  flexShrink: 0,
                  color: isCurrentPlan ? "#6EE7B7" : isPremiumCard ? "#a5b4fc" : "#4F6EF7",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isActiveCard ? "rgba(255,255,255,0.8)" : "#475569",
                  lineHeight: 1.6,
                  fontSize: "0.88rem",
                }}
              >
                {f}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Book a Demo CTA — opens the Contact Us page */}
        {!showPlanCta && (
          <Button
            fullWidth
            onClick={() => navigate("/contact")}
            sx={{
              py: 1.5,
              borderRadius: "50px",
              fontWeight: 700,
              color: "#fff",
              ...(isActiveCard
                ? {
                    background: "rgba(255,255,255,0.15)",
                    border: "1.5px solid rgba(255,255,255,0.35)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    "&:hover": { background: "rgba(255,255,255,0.22)", boxShadow: "none" },
                  }
                : {
                    background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
                    boxShadow: "0 8px 24px rgba(79,110,247,0.3)",
                    "&:hover": { background: "linear-gradient(135deg, #0D1B4B 0%, #3A5AE8 100%)" },
                  }),
            }}
          >
            Book a Demo
          </Button>
        )}

        {/* CTA Button — always white text so spinner is always white */}
        {showPlanCta && (
        <Button
          fullWidth
          disabled={buttonDisabled || loading}
          onClick={buttonAction}
          sx={{
            py: 1.5,
            borderRadius: "50px",
            fontWeight: 700,
            color: "#fff",
            ...(isCurrentPlan
              ? {
                  background: "rgba(255,255,255,0.15)",
                  border: "1.5px solid rgba(255,255,255,0.35)",
                  "&:hover": { background: "rgba(255,255,255,0.22)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.1)", border: "1.5px solid rgba(255,255,255,0.2)" },
                }
              : isPremiumCard
                ? {
                    background: "rgba(255,255,255,0.15)",
                    border: "1.5px solid rgba(255,255,255,0.35)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    "&:hover": { background: "rgba(255,255,255,0.22)", boxShadow: "none" },
                  }
                : {
                    background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
                    boxShadow: "0 8px 24px rgba(79,110,247,0.3)",
                    "&:hover": { background: "linear-gradient(135deg, #0D1B4B 0%, #3A5AE8 100%)" },
                  }),
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : buttonText}
        </Button>
        )}
      </Box>
    </Box>
  );
};

/* =========================
   MAIN COMPONENT
========================= */
const PricingPlan = () => {
  const [selectedVariant, setSelectedVariant] = useState({});
  const navigate = useNavigate();
  const [tab] = useState(0);          // school plans hidden; always show parent plans
  const [billing, setBilling] = useState("yearly");
  const [students, setStudents] = useState(500);
  const [openLogin, setOpenLogin] = useState(false);
  const hidePlanTab = true;

  const getDiscount = (count) => {
    if (count >= 2000) return 0.15;
    if (count >= 1000) return 0.1;
    return 0;
  };

  const storedToken = localStorage.getItem("parentToken");
  const storedParent = localStorage.getItem("parentInfo");
  const isLoggedIn = !!storedToken && !!storedParent;
  const parentDetails = storedParent ? JSON.parse(storedParent) : null;
  const currentPlanPrice = parentDetails?.planPrice || 0;
  const [parentData, setParentData] = useState(parentDetails);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (plan, variant) => {
    const res = await loadRazorpayScript();
    if (!res) { alert("Payment gateway failed to load."); return; }
    setLoading(true);
    try {
      const res = await createSubscription({ parent_plan_id: plan.id, plan_id: variant.id, total_count: 12 }, storedToken);
      const data = res.response;
      const options = {
        key: RAZORPAY_KEY_ID,
        subscription_id: data.subscription_id,
        name: "MySchoolling – A product of Versynix Global Pvt. Ltd.",
        description: plan.subtitle,
        handler: async function (response) {
          try {
            const verify = await verifySubscription(storedToken);
            if (verify.meta?.success) {
              const updateparent = { ...parentData, planPrice: plan.price, planName: plan.title };
              setParentData(updateparent);
              localStorage.setItem("parentInfo", JSON.stringify(updateparent));
              alert(`Plan purchased successfully! Payment ID: ${response.razorpay_payment_id}`);
            } else {
              alert("Failed to purchase/renew plan! Please try again.");
            }
          } catch (error) {
            alert("Failed to purchase/renew plan! Please try again.");
          }
        },
        prefill: { name: parentData?.name || "", email: parentData?.email || "", contact: parentData?.mobileNo || "" },
        theme: { color: "#4F6EF7" },
        modal: { ondismiss: function () { document.body.style.overflow = ""; document.documentElement.style.overflow = ""; } },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(160deg, #F8FAFF 0%, #EEF2FF 60%, #F0F7FF 100%)",
        }}
      >
        <Box sx={{ position:"absolute", top:-100, right:-100, width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(79,110,247,0.1) 0%,transparent 70%)", pointerEvents:"none" }} />

        <Container maxWidth="md" sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.8,
              borderRadius: "100px",
              bgcolor: "rgba(79,110,247,0.08)",
              border: "1px solid rgba(79,110,247,0.2)",
              mb: 3,
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
              PRICING
            </Typography>
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.2rem", md: "3.4rem" },
              fontWeight: 900,
              color: "#0D1B4B",
              mb: 2.5,
            }}
          >
            Flexible Pricing for{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Every Need
            </Box>
          </Typography>

          <Typography
            variant="h6"
            color="#64748B"
            sx={{ maxWidth: 520, mx: "auto", lineHeight: 1.8, fontWeight: 400 }}
          >
            Transparent. Scalable. Powerful. Choose the plan that fits your school.
          </Typography>
        </Container>
      </Box>

      {/* ===== PLANS ===== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg" id="plans">
          {/* Tab switcher intentionally hidden — school plans not shown */}

          {/* ================= PARENTS ================= */}
          {tab === 0 && (
            <Grid container spacing={3} justifyContent="center" alignItems="stretch">
              {parentPlans.map((plan) => (
                <Grid item xs={12} sm={8} md={4} key={plan.id} sx={{ display: "flex" }}>
                  <Box sx={{ width: "100%", pt: plan.tag ? 3 : 0 }}>
                    <ParentPlanCard
                      plan={plan}
                      selectedVariant={selectedVariant}
                      setSelectedVariant={setSelectedVariant}
                      isLoggedIn={isLoggedIn}
                      currentPlanPrice={currentPlanPrice}
                      storedToken={storedToken}
                      parentData={parentData}
                      setParentData={setParentData}
                      handleSubscribe={handleSubscribe}
                      loading={loading}
                      openLogin={openLogin}
                      setOpenLogin={setOpenLogin}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* ================= SCHOOLS ================= */}
          {tab === 1 && (
            <>
              {/* Billing toggle */}
              <Box display="flex" justifyContent="center" mb={6}>
                <Box
                  sx={{
                    p: 0.6,
                    borderRadius: "14px",
                    bgcolor: "#F1F5FE",
                    border: "1.5px solid rgba(13,27,75,0.07)",
                    display: "inline-flex",
                  }}
                >
                  <ToggleButtonGroup
                    value={billing}
                    exclusive
                    onChange={(_, val) => val && setBilling(val)}
                    sx={{
                      "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "10px !important",
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        fontSize: "0.88rem",
                        color: "#64748B",
                        textTransform: "none",
                        "&.Mui-selected": {
                          bgcolor: "#fff",
                          color: "#0D1B4B",
                          boxShadow: "0 2px 8px rgba(13,27,75,0.1)",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                    <ToggleButton value="quarterly">Quarterly</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* Student count input */}
              <Box textAlign="center" mb={7}>
                <TextField
                  type="number"
                  label="Number of Students"
                  value={students}
                  onChange={(e) => setStudents(Number(e.target.value))}
                  sx={{
                    maxWidth: 260,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      bgcolor: "#fff",
                      "& fieldset": { borderColor: "rgba(13,27,75,0.1)", borderWidth: "1.5px" },
                      "&:hover fieldset": { borderColor: "rgba(79,110,247,0.4)" },
                      "&.Mui-focused fieldset": { borderColor: "#4F6EF7", borderWidth: "2px" },
                    },
                  }}
                />
                <Typography variant="caption" display="block" mt={1.5} color="#64748B" fontWeight={500}>
                  10% off for 1,000+ students · 15% off for 2,000+ students
                </Typography>
              </Box>

              <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                {schoolPlans.map((plan) => {
                  const discount = getDiscount(students);
                  const yearlyAfterDiscount = plan.yearlyPrice * (1 - discount);
                  const pricePerPeriod = billing === "yearly" ? yearlyAfterDiscount : yearlyAfterDiscount / 4;
                  const total = billing === "yearly" ? students * yearlyAfterDiscount : students * (yearlyAfterDiscount / 4);
                  const monthlyEquivalent = (yearlyAfterDiscount / 12).toFixed(2);
                  const isPopular = plan.id === "growth";

                  return (
                    <Grid item xs={12} sm={6} md={4} key={plan.id} sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "100%",
                          borderRadius: "24px",
                          background: isPopular
                            ? "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)"
                            : "#fff",
                          p: isPopular ? "2px" : 0,
                          boxShadow: isPopular
                            ? "0 20px 60px rgba(79,110,247,0.3)"
                            : "0 4px 20px rgba(13,27,75,0.07)",
                          border: isPopular ? "none" : "1.5px solid rgba(13,27,75,0.07)",
                          transition: "all 0.35s ease",
                          "&:hover": { transform: "translateY(-8px)", boxShadow: isPopular ? "0 30px 80px rgba(79,110,247,0.4)" : "0 20px 60px rgba(13,27,75,0.12)" },
                          position: "relative",
                        }}
                      >
                        {isPopular && (
                          <Box sx={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 2, py: 0.6, borderRadius: "100px", background: "#fff", color: "#4F6EF7", fontWeight: 700, fontSize: "0.78rem", boxShadow: "0 4px 16px rgba(79,110,247,0.3)", whiteSpace: "nowrap" }}>
                              <AutoAwesome sx={{ fontSize: 13 }} />
                              {plan.badge}
                            </Box>
                          </Box>
                        )}

                        <Box
                          sx={{
                            height: "100%",
                            p: { xs: 3.5, md: 4 },
                            borderRadius: isPopular ? "22px" : "24px",
                            bgcolor: isPopular ? "#0D1B4B" : "#fff",
                            display: "flex",
                            flexDirection: "column",
                            pt: isPopular ? 5 : { xs: 3.5, md: 4 },
                          }}
                        >
                          <Typography variant="h5" fontWeight={800} color={isPopular ? "#fff" : "#0D1B4B"} mb={1}>
                            {plan.title}
                          </Typography>

                          <Box display="flex" alignItems="baseline" gap={0.5} mb={0.5}>
                            <Typography sx={{ fontSize: "2.2rem", fontWeight: 900, color: isPopular ? "#fff" : "#0D1B4B", lineHeight: 1 }}>
                              ₹{pricePerPeriod.toFixed(0)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isPopular ? "rgba(255,255,255,0.5)" : "#94a3b8" }}>
                              / student / {billing === "yearly" ? "year" : "quarter"}
                            </Typography>
                          </Box>

                          <Typography variant="caption" sx={{ color: isPopular ? "rgba(255,255,255,0.5)" : "#94a3b8", mb: 1 }}>
                            ₹{monthlyEquivalent} / student / month equivalent
                          </Typography>

                          {discount > 0 && (
                            <Chip
                              label={`${discount * 100}% Bulk Discount`}
                              size="small"
                              sx={{ mt: 1, mb: 1, bgcolor: "rgba(16,185,129,0.15)", color: "#10B981", fontWeight: 700, alignSelf: "flex-start" }}
                            />
                          )}

                          <Typography fontWeight={700} color={isPopular ? "#a5b4fc" : "#4F6EF7"} mb={1} fontSize="0.9rem">
                            ₹{total.toLocaleString()} / {billing === "yearly" ? "year" : "quarter"} total
                          </Typography>

                          <Divider sx={{ my: 2.5, borderColor: isPopular ? "rgba(255,255,255,0.1)" : "rgba(13,27,75,0.07)" }} />

                          <Stack spacing={1.5} flex={1} mb={4}>
                            {plan.features.map((f, i) => (
                              <Box key={i} display="flex" alignItems="flex-start" gap={1.5}>
                                <Check sx={{ fontSize: 18, mt: 0.1, flexShrink: 0, color: isPopular ? "#a5b4fc" : "#4F6EF7" }} />
                                <Typography variant="body2" sx={{ color: isPopular ? "rgba(255,255,255,0.75)" : "#475569", lineHeight: 1.6, fontSize: "0.88rem" }}>
                                  {f}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>

                          <Button
                            fullWidth
                            variant="contained"
                            sx={isPopular ? {
                              py: 1.5, borderRadius: "50px", fontWeight: 700,
                              background: "#fff", color: "#0D1B4B",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                              "&:hover": { background: "#EEF2FF", boxShadow: "none" },
                            } : {
                              py: 1.5, borderRadius: "50px", fontWeight: 700,
                              background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
                              boxShadow: "0 8px 24px rgba(79,110,247,0.3)",
                            }}
                            onClick={() => navigate("/contact")}
                          >
                            Book a Demo
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}

                {/* Custom plan */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      width: "100%",
                      p: { xs: 3.5, md: 4 },
                      borderRadius: "24px",
                      border: "1.5px dashed rgba(79,110,247,0.3)",
                      bgcolor: "rgba(79,110,247,0.03)",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      "&:hover": { transform: "translateY(-6px)", bgcolor: "rgba(79,110,247,0.06)", borderColor: "rgba(79,110,247,0.5)" },
                    }}
                  >
                    <Chip label="Enterprise" sx={{ alignSelf: "flex-start", mb: 2, bgcolor: "rgba(79,110,247,0.1)", color: "#4F6EF7", fontWeight: 700 }} />
                    <Typography variant="h5" fontWeight={800} color="#0D1B4B" mb={1}>{customPlan.title}</Typography>
                    <Typography variant="h5" color="#4F6EF7" fontWeight={700} mb={1}>Contact for Pricing</Typography>
                    <Typography variant="body2" color="#64748B" mb={3}>Tailored for large institutions with custom requirements.</Typography>

                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={1.5} flex={1} mb={4}>
                      {customPlan.features.map((f, i) => (
                        <Box key={i} display="flex" alignItems="flex-start" gap={1.5}>
                          <Check sx={{ fontSize: 18, mt: 0.1, flexShrink: 0, color: "#4F6EF7" }} />
                          <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6, fontSize: "0.88rem" }}>{f}</Typography>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        py: 1.5, borderRadius: "50px", fontWeight: 700,
                        borderColor: "#4F6EF7", color: "#4F6EF7", borderWidth: "2px",
                        "&:hover": { borderWidth: "2px", bgcolor: "rgba(79,110,247,0.06)" },
                      }}
                      onClick={() => navigate("/contact")}
                    >
                      Contact Us
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Container>
      </Box>

      <ParentLoginDialog open={openLogin} handleClose={() => setOpenLogin(false)} />
    </Box>
  );
};

export default PricingPlan;
